// Dialog HOÀN HÀNG — khách trả lại hàng của một đơn đã bán.
// Chọn từng mặt hàng + số lượng (+ đúng IMEI với hàng quản lý theo máy),
// hệ thống tự tính tiền hoàn theo giá khách đã trả và ưu tiên trừ công nợ.
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { apiPost } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface Serial {
  id: string;
  serialNo: string;
}
interface OrderItem {
  id: string;
  variantId: string;
  quantity: number;
  unitPrice: string;
  discount: string;
  lineTotal: string;
  productName?: string | null;
  sku?: string | null;
  serialTracked?: boolean;
  serials?: Serial[];
}

interface Props {
  orderId: string;
  orderCode: string;
  items: OrderItem[];
  /** SL đã hoàn ở các phiếu trước, theo orderItemId. */
  returnedQty: Record<string, number>;
  /** Tỷ lệ tiền thực thu / giá trị hàng (chiết khấu cả đơn). */
  ratio: number;
  onClose: () => void;
  onDone: () => void;
}

function fmtVND(n: number) {
  return new Intl.NumberFormat("vi-VN").format(Math.round(n)) + " ₫";
}

export function ReturnDialog({
  orderId,
  orderCode,
  items,
  returnedQty,
  ratio,
  onClose,
  onDone,
}: Props) {
  const qc = useQueryClient();
  // qty[itemId] = số lượng trả; picked[itemId] = tập IMEI được chọn.
  const [qty, setQty] = useState<Record<string, number>>({});
  const [picked, setPicked] = useState<Record<string, Set<string>>>({});
  const [refundMethod, setRefundMethod] = useState<"cash" | "bank_manual">("cash");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const rows = items.map((it) => {
    const already = returnedQty[it.id] ?? 0;
    const remaining = it.quantity - already;
    const unit = it.quantity > 0 ? (parseFloat(it.lineTotal) || 0) / it.quantity : 0;
    return { it, already, remaining, unit };
  });

  const totalRefund = useMemo(
    () =>
      rows.reduce((sum, r) => sum + r.unit * (qty[r.it.id] ?? 0), 0) * ratio,
    [rows, qty, ratio],
  );

  function setQtyFor(itemId: string, value: number, remaining: number, serials: Serial[]) {
    const v = Math.max(0, Math.min(value, remaining));
    setQty((prev) => ({ ...prev, [itemId]: v }));
    setPicked((prev) => {
      // Trả đúng bằng số máy còn lại của dòng → tự tích hết, khỏi bấm tay
      // (trường hợp phổ biến nhất: dòng chỉ có 1 máy).
      if (v > 0 && serials.length === v) {
        return { ...prev, [itemId]: new Set(serials.map((s) => s.id)) };
      }
      // Bỏ bớt IMEI đã chọn nếu vượt số lượng mới.
      const cur = prev[itemId];
      if (!cur || cur.size <= v) return prev;
      return { ...prev, [itemId]: new Set(Array.from(cur).slice(0, v)) };
    });
  }

  // Khách trả nguyên đơn (hay gặp: cả bộ điều hòa cục nóng + cục lạnh).
  function selectAll() {
    const nextQty: Record<string, number> = {};
    const nextPicked: Record<string, Set<string>> = {};
    for (const r of rows) {
      if (r.remaining <= 0) continue;
      nextQty[r.it.id] = r.remaining;
      const serials = r.it.serials ?? [];
      if (serials.length === r.remaining) {
        nextPicked[r.it.id] = new Set(serials.map((s) => s.id));
      }
    }
    setQty(nextQty);
    setPicked(nextPicked);
  }

  function toggleSerial(itemId: string, serialId: string, maxQty: number) {
    setPicked((prev) => {
      const cur = new Set(prev[itemId] ?? []);
      if (cur.has(serialId)) {
        cur.delete(serialId);
      } else {
        if (cur.size >= maxQty) return prev; // không chọn quá số lượng trả
        cur.add(serialId);
      }
      return { ...prev, [itemId]: cur };
    });
  }

  const createMut = useMutation({
    mutationFn: () =>
      apiPost(`/v1/orders/${orderId}/returns`, {
        lines: rows
          .filter((r) => (qty[r.it.id] ?? 0) > 0)
          .map((r) => ({
            orderItemId: r.it.id,
            quantity: qty[r.it.id],
            serialIds: Array.from(picked[r.it.id] ?? []),
          })),
        refundMethod,
        reason: reason.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["order", orderId] });
      qc.invalidateQueries({ queryKey: ["order-returns", orderId] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["stock"] });
      onDone();
    },
    onError: (e) => setError(translateApiError(e)),
  });

  const anyPicked = rows.some((r) => (qty[r.it.id] ?? 0) > 0);
  // Hàng quản lý theo IMEI: số IMEI chọn phải khớp số lượng trả. Liệt kê hẳn
  // dòng nào còn thiếu để người dùng biết vì sao nút bị khóa.
  const missingSerialRows = rows.filter((r) => {
    const q = qty[r.it.id] ?? 0;
    if (q === 0) return false;
    if ((r.it.serials?.length ?? 0) === 0) return false;
    return (picked[r.it.id]?.size ?? 0) !== q;
  });
  const serialMismatch = missingSerialRows.length > 0;

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-background">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h2 className="text-lg font-semibold">Hoàn hàng — đơn {orderCode}</h2>
        <button type="button" onClick={onClose} aria-label="Đóng" className="rounded p-1 hover:bg-muted">
          <X size={20} />
        </button>
      </div>

      <div className="flex-1 space-y-3 overflow-auto px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">
            Chọn mặt hàng khách trả lại. Hàng sẽ được nhập lại kho đã bán, tiền hoàn
            ưu tiên trừ vào công nợ của khách, phần còn lại mới trả bằng tiền.
          </p>
          <Button type="button" variant="outline" size="sm" onClick={selectAll}>
            Trả toàn bộ đơn
          </Button>
        </div>

        {rows.map(({ it, already, remaining, unit }) => {
          const q = qty[it.id] ?? 0;
          const serials = it.serials ?? [];
          return (
            <div key={it.id} className="rounded-md border p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium">{it.productName ?? "—"}</p>
                  <p className="text-xs text-muted-foreground">
                    {it.sku ?? "—"} · đã mua {it.quantity}
                    {already > 0 && ` · đã hoàn ${already}`} · đơn giá {fmtVND(unit)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Trả lại</span>
                  <Input
                    type="number"
                    min={0}
                    max={remaining}
                    value={q === 0 ? "" : String(q)}
                    placeholder="0"
                    disabled={remaining <= 0}
                    onChange={(e) =>
                      setQtyFor(it.id, parseInt(e.target.value, 10) || 0, remaining, serials)
                    }
                    className="h-8 w-20 text-center"
                  />
                  <span className="text-xs text-muted-foreground">/ {remaining}</span>
                </div>
              </div>

              {q > 0 && serials.length > 0 && (
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-muted-foreground">
                    Chọn đúng máy khách trả ({picked[it.id]?.size ?? 0}/{q}):
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {serials.map((s) => {
                      const on = picked[it.id]?.has(s.id) ?? false;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => toggleSerial(it.id, s.id, q)}
                          className={`rounded border px-2 py-0.5 font-mono text-xs ${
                            on ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted"
                          }`}
                        >
                          {s.serialNo}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {remaining <= 0 && (
                <p className="mt-1 text-xs text-muted-foreground">Mặt hàng này đã hoàn hết.</p>
              )}
            </div>
          );
        })}

        <div className="space-y-2 rounded-md border p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">Phần phải trả bằng tiền:</span>
            {(["cash", "bank_manual"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setRefundMethod(m)}
                className={`rounded-md border px-3 py-1.5 text-sm ${
                  refundMethod === m ? "border-primary bg-primary text-primary-foreground" : ""
                }`}
              >
                {m === "cash" ? "Tiền mặt" : "Chuyển khoản"}
              </button>
            ))}
          </div>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Lý do khách trả hàng (tùy chọn)"
          />
          <p className="text-sm">
            Giá trị hàng hoàn:{" "}
            <span className="font-semibold text-primary">{fmtVND(totalRefund)}</span>
          </p>
          <p className="text-xs text-muted-foreground">
            Nếu khách đang nợ, số này trừ vào công nợ trước; phần vượt quá công nợ
            mới trả lại bằng {refundMethod === "cash" ? "tiền mặt" : "chuyển khoản"} và
            hiện ở báo cáo Quỹ tiền (chiều CHI).
          </p>
        </div>

        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
        )}
      </div>

      <div className="space-y-2 border-t px-4 py-3">
        {/* Nói rõ vì sao nút bị khóa — trước đây chỉ có tooltip, người dùng
            tưởng hệ thống lỗi (Sếp báo 12/08). */}
        {serialMismatch && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
            Còn thiếu IMEI: bấm chọn máy khách trả ở{" "}
            {missingSerialRows
              .map((r) => `${r.it.productName ?? "mặt hàng"} (${picked[r.it.id]?.size ?? 0}/${qty[r.it.id] ?? 0})`)
              .join(", ")}
            .
          </p>
        )}
        {!anyPicked && !serialMismatch && (
          <p className="text-sm text-muted-foreground">
            Nhập số lượng ở ô "Trả lại" của mặt hàng khách mang tới để tiếp tục.
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={createMut.isPending}>
            Hủy
          </Button>
          <Button
            disabled={!anyPicked || serialMismatch || createMut.isPending}
            onClick={() => createMut.mutate()}
          >
            {createMut.isPending ? "Đang xử lý…" : "Xác nhận hoàn hàng"}
          </Button>
        </div>
      </div>
    </div>
  );
}
